// ============================================================
// Reading characters out of a folder, in bulk.
//
// THE LATEST ITERATION IS THE CHARACTER. A character folder is a
// timeline - `001.json`, `002.json`, `007-taller-walls.json` - and
// bulk import wants the current one, not all of them. The Workshop is
// where you go back through the history.
//
// EVERY FILE GOES THROUGH `normalizeCharacterExport`, which is the only
// reader in the app that copes with all three shapes a character file
// comes in (a bare object, a raw array, a `{version, characters}`
// envelope) and reports what it had to fix. The session's own two
// importers each handle a different subset and disagree with each
// other: one throws on a bare object, the other pushes an envelope in
// as if it were a character. Neither is used here (v8.16.0).
//
// A BAD FILE IS ONE FILE. Import twenty and the nineteen good ones
// arrive; the failure is named and counted rather than aborting the
// batch or vanishing into `console.error`, which is what the old
// single-file import did.
// ============================================================
import { normalizeCharacterExport } from '../engine/importValidation';
import type { FileCharacter } from './folderFiles';
import type { Character } from '../engine/character/types';
import type { ValidationWarning } from '../engine/importValidation';

export interface FolderImport {
  characters: Character[];
  /** What each file needed fixing, keyed by the character folder it came from. */
  warnings: { from: string; warnings: ValidationWarning[] }[];
  /** Folders that could not be read at all, or held nothing usable. */
  failed: string[];
}

/** What one entry yielded: its characters and what had to be fixed, or null when it held nothing usable. */
type EntryRead = { characters: Character[]; warnings?: ValidationWarning[] } | null;

/**
 * Read each entry in turn and gather the report. A read that throws or
 * yields nothing names the entry in `failed`; the rest still arrive.
 */
async function collect<E extends { name: string }>(
  entries: readonly E[],
  read: (entry: E) => Promise<EntryRead>,
): Promise<FolderImport> {
  const characters: Character[] = [];
  const warnings: FolderImport['warnings'] = [];
  const failed: string[] = [];
  for (const entry of entries) {
    const got = await read(entry).catch(() => null);
    if (!got) {
      failed.push(entry.name);
      continue;
    }
    characters.push(...got.characters);
    if (got.warnings?.length) warnings.push({ from: entry.name, warnings: got.warnings });
  }
  return { characters, warnings, failed };
}

/**
 * A file INPUT's entry. A file may hold several - an envelope export
 * does. All of them come in; the entry is one FOLDER, not one character.
 */
async function readFileEntry(entry: FileCharacter): Promise<EntryRead> {
  const parsed = normalizeCharacterExport(JSON.parse(await entry.file.text()));
  return parsed.characters.length ? { characters: parsed.characters, warnings: parsed.warnings } : null;
}

/**
 * Read each entry of a tree that came from a directory INPUT: a bad file is
 * one file, named and counted, and the other nineteen still arrive.
 *
 * Sequential: two hundred files opened at once is a thing the browser may
 * refuse, and bulk import is done once. (Its sibling for the folder
 * Workshop's file-system handles retired with the Workshop, assets stage 4.)
 */
export async function readCharactersFromFiles(entries: FileCharacter[]): Promise<FolderImport> {
  return collect(entries, readFileEntry);
}
